import {
  Avatar,
  ListItem,
  TextField,
  Autocomplete,
  ListItemText,
  ListItemAvatar,
  createFilterOptions
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import type { SyntheticEvent, Dispatch, SetStateAction } from "react";
import type { AdditionalUser } from "../../../../../@types/listings/user";

type OwnersPickerProps = {
  type: "new" | "close" | "filter";
  loading?: boolean;
  required?: boolean;
  ownersData: AdditionalUserWithHint[];
  organizationUsers: AdditionalUserWithHint[];
  onOwnersChange: Dispatch<SetStateAction<AdditionalUser[]>>;
};

type AdditionalUserWithHint = AdditionalUser & { hint?: boolean };

export function OwnersPicker({
  type,
  loading,
  required,
  ownersData,
  organizationUsers,
  onOwnersChange
}: OwnersPickerProps): JSX.Element {
  const { t: translate } = useTranslation();

  const isWithLimit = ["new"].includes(type);

  const isCloseDeal = type === "close";

  const handleOwnersChange = (
    _event: SyntheticEvent,
    newOwnersData: null | AdditionalUserWithHint[]
  ): void => {
    if (isWithLimit) {
      const newOwnersDataEmpty = !newOwnersData?.length;
      const newOwnersDataMoreThanTwo = newOwnersData?.length > 2;

      if (newOwnersDataEmpty || newOwnersDataMoreThanTwo) return;
    }

    if (isCloseDeal && newOwnersData.length === 2) {
      const mappedData = newOwnersData.map((item) =>
        typeof item === "string" ? item : item.email
      );

      const [firstItem, secondItem] = mappedData;

      const isDuplicated = firstItem === secondItem;

      if (isDuplicated) {
        onOwnersChange([]);
        return;
      }
    }

    onOwnersChange(newOwnersData);
  };

  const isHittingLimit = ownersData.length === 2;
  const isFieldRequired = required && !ownersData.length;

  return (
    <Autocomplete
      multiple
      fullWidth
      value={ownersData}
      options={organizationUsers}
      freeSolo={isCloseDeal}
      renderInput={(params) => (
        <TextField
          {...params}
          label={translate(`listingsPage.tableColumns.${isCloseDeal ? "buyerOrigin" : "owners"}`)}
          required={isFieldRequired}
          onKeyDown={(e) => e.key === "Enter" && isFieldRequired && e.preventDefault()}
        />
      )}
      renderOption={(props, { name, email, avatarUrl, hint }) => {
        const isIncluded = ownersData.some((owner) => owner.email === email);
        const isDisabled = isWithLimit && isHittingLimit && !isIncluded;

        return (
          <ListItem {...props} disabled={isDisabled}>
            <ListItemAvatar>
              <Avatar src={avatarUrl} />
            </ListItemAvatar>
            {hint ? (
              <ListItemText primary={`${translate("listingsPage.control.add")} ${email}`} />
            ) : (
              <ListItemText primary={name ?? email} secondary={name ? email : undefined} />
            )}
          </ListItem>
        );
      }}
      disabled={loading}
      getOptionLabel={({ email, name }) => email ?? name}
      isOptionEqualToValue={(option, value) => option.email === value.email}
      onChange={handleOwnersChange}
      filterOptions={(options, params) => {
        const filtered = filterOptions(options, params);

        if (!isCloseDeal) return filtered;

        const { inputValue } = params;

        const isExisting = options.some((option) => inputValue === option.email);
        const isIncluded = ownersData.some((owner) => owner.email === inputValue);

        const trimmedInputValue = inputValue.trim();

        if (trimmedInputValue && !isHittingLimit && !isExisting && !isIncluded)
          filtered.push({ email: trimmedInputValue, hint: true });

        return filtered;
      }}
    />
  );
}

const filterOptions = createFilterOptions<AdditionalUserWithHint>({
  matchFrom: "any",
  stringify: ({ name, email }) => name + email
});
